import styled from "styled-components";
import { Link } from "react-router-dom";
import {
  siteDarkGreen,
  siteDarkBlue,
  siteLightBlue,
  siteLightGreen,
  siteYellow,
  sitePurple
} from "../../providers/Theme";
import {
  bodyFontBase,
  bodyFontMd,
  buttonFontSize,
  sectionLabelSize
} from "../shared.styled.components";
import HolographicBg from "../../assets/KRF-holographic-bg.jpg";

export const HeroSection = styled.section`
  display: flex;
  flex-direction: column;

  ${({ theme }) => theme.md`
    flex-direction: row;
    min-height: 480px;
  `}
`;

export const LeftPanel = styled.div`
  background: ${siteDarkGreen};
  padding: 3rem 2rem 3.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${({ theme }) => theme.md`
    flex: 0 0 42%;
    padding: 4rem 3rem 4rem 4rem;
  `}

  ${({ theme }) => theme.lg`
    flex: 0 0 44%;
    padding: 4rem 3.5rem 4rem 5rem;
  `}
`;

export const LeftHeading = styled.h1`
  color: white;
  font-weight: 800;
  font-size: 2.4rem;
  line-height: 1.1;
  margin-bottom: 1.5rem;

  ${({ theme }) => theme.md`
    font-size: 2.8rem;
  `}

  ${({ theme }) => theme.lg`
    font-size: 3.2rem;
  `}
`;

export const LeftBody = styled.p`
  color: white;
  font-size: ${bodyFontBase};
  line-height: 1.7;
  margin-bottom: 2rem;

  ${({ theme }) => theme.md`
    font-size: ${bodyFontMd};
  `}
`;

export const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  max-width: 220px;
`;

export const GhostButton = styled(Link)`
  display: block;
  color: white;
  font-weight: 700;
  font-size: ${buttonFontSize};
  text-align: center;
  text-decoration: none;
  border: 2px solid white;
  border-radius: 0.5rem;
  padding: 0.7rem 1.5rem;
  transition:
    background 0.15s ease,
    color 0.15s ease;

  &:hover {
    background: white;
    color: ${siteDarkGreen};
  }
`;

export const RightPanel = styled.div`
  background: white;
  padding: 3rem 2rem 3.5rem;

  ${({ theme }) => theme.md`
    flex: 1;
    padding: 4rem 4rem 4rem 3rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
  `}

  ${({ theme }) => theme.lg`
    padding: 4rem 5rem 4rem 3.5rem;
  `}
`;

export const RightTopHeading = styled.h2`
  color: ${siteDarkGreen};
  font-weight: 800;
  font-size: 1.6rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-bottom: 0.5rem;

  ${({ theme }) => theme.md`
    font-size: 1.8rem;
  `}
`;

export const RightSubheading = styled.p`
  color: ${siteDarkBlue};
  font-weight: 700;
  font-size: ${bodyFontBase};
  line-height: 1.5;
  margin-bottom: 1.75rem;

  ${({ theme }) => theme.md`
    font-size: ${bodyFontMd};
  `}
`;

export const StatsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

export const StatItem = styled.div``;

export const StatNumber = styled.div<{ $color: string }>`
  color: ${({ $color }) => $color};
  font-weight: 800;
  font-size: 2.8rem;
  line-height: 1;
  margin-bottom: 0.3rem;
`;

export const StatText = styled.p`
  color: #444;
  font-size: ${bodyFontBase};
  line-height: 1.55;
  margin: 0;
`;

// ── Vision Section ────────────────────────────────────────────────────────────

export const VisionSection = styled.section`
  background: white;
  padding: 3.5rem 2rem;

  ${({ theme }) => theme.md`
    padding: 4rem;
  `}

  ${({ theme }) => theme.lg`
    padding: 4rem 5rem;
  `}
`;

export const VisionInner = styled.div`
  max-width: 72rem;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 2rem;

  ${({ theme }) => theme.lg`
    flex-direction: row;
    align-items: center;
    gap: 3rem;
  `}
`;

export const VisionLeft = styled.div`
  ${({ theme }) => theme.lg`
    flex: 0 0 58%;
  `}
`;

export const VisionLabel = styled.h2`
  color: ${siteDarkGreen};
  font-weight: 800;
  font-size: ${sectionLabelSize};
  text-transform: uppercase;
  margin-bottom: 0.75rem;
`;

export const VisionHeadline = styled.p`
  color: ${siteDarkBlue};
  font-weight: 700;
  font-size: 1.35rem;
  line-height: 1.4;
  margin-bottom: 0.85rem;

  ${({ theme }) => theme.md`
    font-size: 1.5rem;
  `}
`;

export const VisionBody = styled.p`
  color: #555;
  font-size: ${bodyFontBase};
  line-height: 1.7;
  margin: 0;
`;

export const VisionCallout = styled.div`
  flex: 1;
  background: #e8f5d6;
  border: 2px solid ${siteLightGreen};
  border-radius: 12px;
  padding: 1.75rem 2rem;
`;

export const VisionCalloutText = styled.p`
  color: ${siteDarkBlue};
  font-weight: 700;
  font-size: 1.25rem;
  line-height: 1.5;
  margin: 0;

  ${({ theme }) => theme.md`
    font-size: 1.35rem;
  `}
`;

// ── What We Do ────────────────────────────────────────────────────────────────

export const WhatWeDoSection = styled.section`
  background: white;
  padding: 3.5rem 2rem 4rem;

  ${({ theme }) => theme.md`
    padding: 4rem;
  `}

  ${({ theme }) => theme.lg`
    padding: 4rem 5rem;
  `}
`;

export const WhatWeDoInner = styled.div`
  max-width: 72rem;
  margin: 0 auto;
`;

export const WhatWeDoLabel = styled.h2`
  color: ${siteDarkBlue};
  font-weight: 800;
  font-size: ${sectionLabelSize};
  text-transform: uppercase;
  margin-bottom: 0.75rem;
`;

export const WhatWeDoSubtitle = styled.p`
  color: #555;
  font-size: ${bodyFontBase};
  line-height: 1.7;
  margin-bottom: 2rem;

  ${({ theme }) => theme.md`
    font-size: ${bodyFontMd};
  `}
`;

export const WhatWeDoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;

  ${({ theme }) => theme.sm`
    grid-template-columns: repeat(2, 1fr);
  `}

  ${({ theme }) => theme.xl`
    grid-template-columns: repeat(4, 1fr);
  `}
`;

export const WhatWeDoCard = styled.div`
  background: #f5faf5;
  border: 1.5px solid ${siteDarkGreen};
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export const WhatWeDoCardAccent = styled.div`
  background: ${siteDarkGreen};
  height: 10px;
  width: 100%;
`;

export const WhatWeDoCardBody = styled.div`
  padding: 1.25rem 1.5rem 1.75rem;
`;

export const WhatWeDoCardHeading = styled.h3`
  color: ${siteDarkBlue};
  font-weight: 700;
  font-size: 1.6rem;
  margin-bottom: 0.75rem;
  line-height: 1.3;
`;

export const WhatWeDoCardText = styled.p`
  color: #444;
  font-size: 1.1rem;
  line-height: 1.65;
  margin: 0;
`;

// ── Our Why ───────────────────────────────────────────────────────────────────

export const WhySection = styled.section`
  background: #eef1f5;
  padding: 3.5rem 2rem;

  ${({ theme }) => theme.md`
    padding: 4rem;
  `}

  ${({ theme }) => theme.lg`
    padding: 4rem 5rem;
  `}
`;

export const WhyInner = styled.div`
  max-width: 72rem;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 2rem;

  ${({ theme }) => theme.lg`
    flex-direction: row;
    align-items: center;
    gap: 3rem;
  `}
`;

export const WhyLeft = styled.div`
  ${({ theme }) => theme.lg`
    flex: 0 0 48%;
  `}
`;

export const WhyLabel = styled.h2`
  color: ${siteDarkBlue};
  font-weight: 800;
  font-size: ${sectionLabelSize};
  text-transform: uppercase;
  margin-bottom: 1.25rem;
`;

export const WhyBody = styled.p`
  color: #444;
  font-size: ${bodyFontBase};
  line-height: 1.7;
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const WhyCard = styled.div`
  flex: 1;
  background: white;
  border-radius: 14px;
  padding: 2rem 2.25rem;
`;

export const WhyCardHeading = styled.h3`
  color: ${siteDarkGreen};
  font-weight: 700;
  font-size: 1.6rem;
  margin-bottom: 1.25rem;
`;

export const WhyCheckList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const WhyCheckItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  color: ${siteDarkBlue};
  font-size: ${bodyFontBase};
  line-height: 1.55;
`;

export const WhyCheckIcon = styled.span`
  flex-shrink: 0;
  margin-top: 0.1rem;
`;

// ── Gradient Divider ──────────────────────────────────────────────────────────

export const GradientDivider = styled.div`
  height: 60px;
  background-image: url(${HolographicBg});
  background-size: cover;
  background-position: center;
  opacity: 0.5;
`;

// ── Mission Band ──────────────────────────────────────────────────────────────

export const MissionBand = styled.div`
  background: ${siteLightGreen};
  padding: 2.25rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  ${({ theme }) => theme.md`
    flex-direction: row;
    align-items: center;
    gap: 3rem;
    padding: 2.25rem 4rem;
  `}

  ${({ theme }) => theme.lg`
    padding: 2.25rem 5rem;
  `}
`;

export const MissionLabel = styled.h2`
  color: white;
  font-weight: 800;
  font-size: 1.8rem;
  white-space: nowrap;
  margin: 0;

  ${({ theme }) => theme.md`
    font-size: 2rem;
  `}
`;

export const MissionText = styled.p`
  color: white;
  font-size: ${bodyFontBase};
  line-height: 1.7;
  margin: 0;

  ${({ theme }) => theme.md`
    font-size: ${bodyFontMd};
  `}
`;

// ── Explore More Nav ──────────────────────────────────────────────────────────

export const ExploreSection = styled.section`
  background: white;
  padding: 3.5rem 2rem 4rem;

  ${({ theme }) => theme.md`
    padding: 4rem;
  `}

  ${({ theme }) => theme.lg`
    padding: 4rem 5rem;
  `}
`;

export const ExploreInner = styled.div`
  max-width: 72rem;
  margin: 0 auto;
`;

export const ExploreHeading = styled.h2`
  color: ${siteDarkBlue};
  font-weight: 800;
  font-size: ${sectionLabelSize};
  text-transform: uppercase;
  margin-bottom: 0.75rem;
`;

export const ExploreSubtitle = styled.p`
  color: #555;
  font-size: ${bodyFontBase};
  line-height: 1.7;
  margin-bottom: 2rem;
`;

export const ExploreGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;

  ${({ theme }) => theme.md`
    grid-template-columns: repeat(2, 1fr);
  `}
`;

export const ExploreCard = styled.div`
  background: white;
  border: 1.5px solid #d8dce8;
  border-radius: 12px;
  padding: 1.5rem 1.75rem;
`;

export const ExploreCardTitle = styled.a`
  display: block;
  color: ${siteDarkGreen};
  font-weight: 700;
  font-size: 1.35rem;
  text-decoration: none;
  margin-bottom: 0.5rem;

  &:hover {
    text-decoration: underline;
    color: ${siteDarkGreen};
  }
`;

export const ExploreCardText = styled.p`
  color: ${siteDarkBlue};
  font-size: ${bodyFontBase};
  line-height: 1.65;
  margin: 0;
`;

export { siteLightBlue, siteYellow, sitePurple };
